/**
 * API Types
 * Centralized type definitions for API responses and errors
 */

/**
 * Standard API response wrapper from the backend
 * All API responses follow this format:
 * {
 *   data: T,
 *   meta: { code: string, message: string, timestamp?: string }
 * }
 */
export interface ApiResponse<T> {
  data: T;
  meta: ApiMeta;
}

/**
 * Standard API response metadata
 */
export interface ApiMeta {
  code: string;
  message: string;
  timestamp?: string;
}

/**
 * Pagination information for paginated API responses
 */
export interface PaginationMeta {
  currentPage: number;
  pageSize: number;
  totalPages: number;
  totalRecords: number;
}

/**
 * Paginated API response wrapper
 */
export interface PaginatedApiResponse<T> {
  data: T[];
  paging: PaginationMeta;
  meta?: ApiMeta;
}

/**
 * API Error response from the backend
 */
export interface ApiErrorResponse {
  meta: {
    code: string;
    message: string;
    errors?: Record<string, string[]>;
  };
}

/**
 * Custom error class for API errors
 */
export class ApiError extends Error {
  public readonly statusCode: number;
  public readonly code: string;
  public readonly errors?: Record<string, string[]>;
  public readonly isNetworkError: boolean;
  public readonly isTimeoutError: boolean;

  constructor(
    message: string,
    statusCode: number,
    code: string,
    options?: {
      errors?: Record<string, string[]>;
      isNetworkError?: boolean;
      isTimeoutError?: boolean;
    }
  ) {
    super(message);
    this.name = 'ApiError';
    this.statusCode = statusCode;
    this.code = code;
    this.errors = options?.errors;
    this.isNetworkError = options?.isNetworkError ?? false;
    this.isTimeoutError = options?.isTimeoutError ?? false;

    // Maintains proper stack trace in V8 environments
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, ApiError);
    }
  }

  /**
   * Check if the error is a client error (4xx)
   */
  public isClientError(): boolean {
    return this.statusCode >= 400 && this.statusCode < 500;
  }

  /**
   * Check if the error is a server error (5xx)
   */
  public isServerError(): boolean {
    return this.statusCode >= 500 && this.statusCode < 600;
  }

  /**
   * Check if the error is an authentication error (401)
   */
  public isUnauthorized(): boolean {
    return this.statusCode === 401;
  }

  /**
   * Check if the error is a forbidden error (403)
   */
  public isForbidden(): boolean {
    return this.statusCode === 403;
  }

  /**
   * Check if the error is a not found error (404)
   */
  public isNotFound(): boolean {
    return this.statusCode === 404;
  }
}

/**
 * Request configuration options
 */
export interface RequestConfig {
  params?: Record<string, string | number | boolean | undefined>;
  headers?: Record<string, string>;
  timeout?: number;
  signal?: AbortSignal;
}

